import { Injectable } from '@angular/core';
import { BuilderComponentType, BuilderJsonOutput, OutputField, OutputPanel, OutputTab } from '../interfaces/builder-screen.models';
import { JsonValidationError, JsonValidationResult } from './builder-screen.service';
import { WebhookConfiguration, WebhookField } from '../../webhook-creator/webhook-creator/webhook-interfaces/configuration';

/**
 * Service dedicado para validação de esquemas JSON do Builder Screen
 * Task 1.2: Service para validar compatibilidade JSON com tipos de componentes
 */
@Injectable({
  providedIn: 'root'
})
export class JsonValidationService {

  // Tipos de componentes válidos
  private readonly validComponentTypes: BuilderComponentType[] = [
    'text', 'textarea', 'number', 'switch', 'select', 'date', 
    'tabs', 'section', 'divider', 'button', 'table', 'keyValue', 
    'keyValueObject', 'tag', 'search', 'querySelect',
    'datagrid', 'dropdown', 'row'  // ✅ CORRIGIDO: tipos faltantes
  ];

  // Propriedades obrigatórias por tipo de componente
  private readonly requiredPropsByType: Record<string, string[]> = {
    'text': ['label'],
    'textarea': ['label'],
    'number': ['label'],
    'switch': ['label'],
    'select': ['label', 'options'],
    'date': ['label'],
    'button': ['label', 'actionType'],
    'table': ['label', 'columns'],
    'keyValue': ['label'],
    'keyValueObject': ['label'],
    'tag': ['label'],
    'search': ['label'],
    'querySelect': ['label', 'queryConfig'],
    'section': ['label'],
    'divider': [],
    'tabs': ['items']
  };

  /**
   * Valida esquema JSON completo com validações simplificadas
   * Foco apenas em erros críticos que impedem o funcionamento
   * Agora suporta tanto BuilderJsonOutput quanto WebhookConfiguration
   */
  validateJsonSchema(jsonData: BuilderJsonOutput | WebhookConfiguration): JsonValidationResult {
    const errors: JsonValidationError[] = [];
    const warnings: string[] = [];

    try {
      // Validação de estrutura básica (apenas o essencial)
      this.validateBasicStructure(jsonData, errors);

      // Validação básica de fields (apenas erros críticos)
      if (jsonData.fields) {
        this.validateFieldsBasic(jsonData.fields, errors, warnings);
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };

    } catch (error: any) {
      return {
        isValid: false,
        errors: [{
          type: 'syntax',
          message: `Erro na validação do esquema: ${error.message || error}`
        }],
        warnings
      };
    }
  }

  /**
   * Validação básica de fields - apenas erros críticos
   * Agora suporta tanto OutputField[] quanto WebhookField[]
   */
  private validateFieldsBasic(fields: OutputField[] | WebhookField[], errors: JsonValidationError[], warnings: string[]): void {
    if (fields.length === 0) {
      warnings.push('Nenhum field definido');
      return;
    }

    fields.forEach((field, index) => {
      // Cast para any para trabalhar com ambos os tipos (OutputField e WebhookField)
      const genericField = field as any;
      
      // Apenas validações críticas que impedem o funcionamento
      if (!genericField.field || typeof genericField.field !== 'string') {
        errors.push({
          type: 'schema',
          message: `Field ${index + 1}: propriedade "field" é obrigatória e deve ser string`,
          componentType: 'field'
        });
      }

      if (!genericField.type || typeof genericField.type !== 'string') {
        errors.push({
          type: 'schema',
          message: `Field ${index + 1}: propriedade "type" é obrigatória e deve ser string`,
          componentType: 'field'
        });
      }

      // Avisar sobre tipos desconhecidos mas não bloquear
      if (genericField.type && !this.validComponentTypes.includes(genericField.type as BuilderComponentType)) {
        warnings.push(`Field ${index + 1}: tipo "${genericField.type}" não é reconhecido, será tratado como campo text`);
      }
    });
  }

  /**
   * Valida estrutura básica do JSON
   */
  private validateBasicStructure(jsonData: any, errors: JsonValidationError[]): void {
    if (!jsonData || typeof jsonData !== 'object') {
      errors.push({
        type: 'syntax',
        message: 'JSON deve ser um objeto válido'
      });
      return;
    }

    // Verificar se tem pelo menos uma estrutura válida
    const hasValidStructure = jsonData.panels || jsonData.tabs || jsonData.fields;
    if (!hasValidStructure) {
      errors.push({
        type: 'schema',
        message: 'JSON deve conter pelo menos panels, tabs ou fields'
      });
    }

    // Verificar tipos das propriedades principais
    if (jsonData.panels && !Array.isArray(jsonData.panels)) {
      errors.push({
        type: 'schema',
        message: 'Propriedade "panels" deve ser um array'
      });
    }

    if (jsonData.tabs && !Array.isArray(jsonData.tabs)) {
      errors.push({
        type: 'schema',
        message: 'Propriedade "tabs" deve ser um array'
      });
    }

    if (jsonData.fields && !Array.isArray(jsonData.fields)) {
      errors.push({
        type: 'schema',
        message: 'Propriedade "fields" deve ser um array'
      });
    }
  }

  /**
   * Valida panels
   */
  private validatePanels(panels: OutputPanel[], errors: JsonValidationError[], warnings: string[]): void {
    if (panels.length === 0) {
      warnings.push('Nenhum panel definido');
      return;
    }

    const panelIds = new Set<string>();

    panels.forEach((panel, index) => {
      // Validar propriedades obrigatórias
      if (!panel.id || typeof panel.id !== 'string') {
        errors.push({
          type: 'schema',
          message: `Panel ${index + 1}: propriedade "id" é obrigatória e deve ser string`,
          componentType: 'panel'
        });
      }

      if (!panel.name || typeof panel.name !== 'string') {
        errors.push({
          type: 'schema',
          message: `Panel ${index + 1}: propriedade "name" é obrigatória e deve ser string`,
          componentType: 'panel'
        });
      }

      // Verificar IDs duplicados
      if (panel.id) {
        if (panelIds.has(panel.id)) {
          errors.push({
            type: 'schema',
            message: `Panel ${index + 1}: ID "${panel.id}" duplicado`,
            componentType: 'panel'
          });
        } else {
          panelIds.add(panel.id);
        }
      }

      // Validar propriedades opcionais
      if (panel.active !== undefined && typeof panel.active !== 'boolean') {
        warnings.push(`Panel ${index + 1}: propriedade "active" deve ser boolean`);
      }

      if (panel.disabled !== undefined && typeof panel.disabled !== 'boolean') {
        warnings.push(`Panel ${index + 1}: propriedade "disabled" deve ser boolean`);
      }
    });
  }

  /**
   * Valida tabs
   */
  private validateTabs(tabs: OutputTab[], errors: JsonValidationError[], warnings: string[]): void {
    if (tabs.length === 0) {
      warnings.push('Nenhuma tab definida');
      return;
    }

    const tabIds = new Set<string>();

    tabs.forEach((tab, index) => {
      // Validar propriedades obrigatórias
      if (!tab.id || typeof tab.id !== 'string') {
        errors.push({
          type: 'schema',
          message: `Tab ${index + 1}: propriedade "id" é obrigatória e deve ser string`,
          componentType: 'tab'
        });
      }

      if (!tab.name || typeof tab.name !== 'string') {
        errors.push({
          type: 'schema',
          message: `Tab ${index + 1}: propriedade "name" é obrigatória e deve ser string`,
          componentType: 'tab'
        });
      }

      // Verificar IDs duplicados
      if (tab.id) {
        if (tabIds.has(tab.id)) {
          errors.push({
            type: 'schema',
            message: `Tab ${index + 1}: ID "${tab.id}" duplicado`,
            componentType: 'tab'
          });
        } else {
          tabIds.add(tab.id);
        }
      }

      // Validar propriedades opcionais
      if (tab.icon && typeof tab.icon !== 'string') {
        warnings.push(`Tab ${index + 1}: propriedade "icon" deve ser string`);
      }

      if (tab.disabled !== undefined && typeof tab.disabled !== 'boolean') {
        warnings.push(`Tab ${index + 1}: propriedade "disabled" deve ser boolean`);
      }
    });
  }

  /**
   * Valida fields com validação específica por tipo
   */
  private validateFields(fields: OutputField[], panels: OutputPanel[] | undefined, errors: JsonValidationError[], warnings: string[]): void {
    if (fields.length === 0) {
      warnings.push('Nenhum field definido');
      return;
    }

    const fieldNames = new Set<string>();

    fields.forEach((field, index) => {
      // Validar propriedades básicas
      if (!field.field || typeof field.field !== 'string') {
        errors.push({
          type: 'schema',
          message: `Field ${index + 1}: propriedade "field" é obrigatória e deve ser string`,
          componentType: 'field'
        });
      }

      if (!field.type || typeof field.type !== 'string') {
        errors.push({
          type: 'schema',
          message: `Field ${index + 1}: propriedade "type" é obrigatória e deve ser string`,
          componentType: 'field'
        });
      }

      // Verificar nomes duplicados
      if (field.field) {
        if (fieldNames.has(field.field)) {
          errors.push({
            type: 'schema',
            message: `Field ${index + 1}: nome "${field.field}" duplicado`,
            componentType: 'field'
          });
        } else {
          fieldNames.add(field.field);
        }
      }

      // Validar tipo de componente
      if (field.type && !this.validComponentTypes.includes(field.type as BuilderComponentType)) {
        warnings.push(`Field ${index + 1}: tipo "${field.type}" não é reconhecido`);
      }

      // Validação de referência de panel removida - confiar na estrutura JSON do usuário

      // Validar propriedades específicas por tipo
      this.validateFieldByType(field, index, errors, warnings);

      // Validar posicionamento
      this.validateFieldPositioning(field, index, warnings);
    });
  }

  /**
   * Valida propriedades específicas por tipo de field
   */
  private validateFieldByType(field: OutputField, index: number, errors: JsonValidationError[], warnings: string[]): void {
    const fieldType = field.type;
    if (!fieldType) return;

    // Verificar propriedades obrigatórias por tipo
    const requiredProps = this.requiredPropsByType[fieldType];
    if (requiredProps) {
      requiredProps.forEach(prop => {
        if (!(field as any)[prop]) {
          warnings.push(`Field ${index + 1} (${fieldType}): propriedade obrigatória "${prop}" não encontrada`);
        }
      });
    }

    // Validações específicas por tipo
    switch (fieldType) {
      case 'select':
      case 'querySelect':
        this.validateSelectField(field as any, index, errors, warnings);
        break;
      
      case 'number':
        this.validateNumberField(field as any, index, warnings);
        break;
      
      case 'table':
        this.validateTableField(field as any, index, errors, warnings);
        break;
      
      case 'button':
        this.validateButtonField(field as any, index, warnings);
        break;
    }
  }

  /**
   * Valida campos do tipo select
   */
  private validateSelectField(field: any, index: number, errors: JsonValidationError[], warnings: string[]): void {
    if (field.options && !Array.isArray(field.options)) {
      errors.push({
        type: 'component',
        message: `Field ${index + 1} (select): propriedade "options" deve ser um array`,
        componentType: 'select'
      });
    }

    if (field.options && Array.isArray(field.options) && field.options.length === 0) {
      warnings.push(`Field ${index + 1} (select): nenhuma opção definida`);
    }
  }

  /**
   * Valida campos do tipo number
   */
  private validateNumberField(field: any, index: number, warnings: string[]): void {
    if (field.min !== undefined && field.max !== undefined && field.min > field.max) {
      warnings.push(`Field ${index + 1} (number): valor mínimo (${field.min}) é maior que o máximo (${field.max})`);
    }
  }

  /**
   * Valida campos do tipo table
   */
  private validateTableField(field: any, index: number, errors: JsonValidationError[], warnings: string[]): void {
    if (field.columns && !Array.isArray(field.columns)) {
      errors.push({
        type: 'component',
        message: `Field ${index + 1} (table): propriedade "columns" deve ser um array`,
        componentType: 'table'
      });
    }

    if (field.columns && Array.isArray(field.columns) && field.columns.length === 0) {
      warnings.push(`Field ${index + 1} (table): nenhuma coluna definida`);
    }
  }

  /**
   * Valida campos do tipo button
   */
  private validateButtonField(field: any, index: number, warnings: string[]): void {
    const validActionTypes = ['submit', 'reset', 'custom', 'navigation', 'api', 'flow', 'save'];
    
    if (field.actionType && !validActionTypes.includes(field.actionType)) {
      warnings.push(`Field ${index + 1} (button): actionType "${field.actionType}" não é reconhecido`);
    }
  }

  /**
   * Valida posicionamento de fields
   */
  private validateFieldPositioning(field: OutputField, index: number, warnings: string[]): void {
    if (field.col !== undefined && (field.col < 0 || field.col > 23)) {
      warnings.push(`Field ${index + 1}: coluna ${field.col} fora do range válido (0-23)`);
    }

    if (field.row !== undefined && field.row < 0) {
      warnings.push(`Field ${index + 1}: linha ${field.row} deve ser >= 0`);
    }

    if (field.colSpan !== undefined && (field.colSpan < 1 || field.colSpan > 24)) {
      warnings.push(`Field ${index + 1}: colSpan ${field.colSpan} fora do range válido (1-24)`);
    }
  }

  /**
   * Valida consistência entre diferentes estruturas
   * REMOVIDO: Validações de referência entre panels/tabs/fields
   * O usuário pode ter estruturas JSON válidas sem essas validações rígidas
   */
  private validateConsistency(jsonData: BuilderJsonOutput, errors: JsonValidationError[], warnings: string[]): void {
    // Validações de consistência removidas - o JSON do usuário está correto como está
    
    // Apenas avisar sobre fields sem panel se for relevante
    if (jsonData.fields) {
      const fieldsWithoutPanel = jsonData.fields.filter(f => !f.panel);
      if (fieldsWithoutPanel.length > 0) {
        // Apenas um aviso informativo, não um erro
        console.info(`${fieldsWithoutPanel.length} field(s) sem panel definido - isso pode ser intencional`);
      }
    }
  }

  /**
   * Valida se um JSON string é válido antes do parse
   */
  validateJsonSyntax(jsonString: string): JsonValidationResult {
    try {
      JSON.parse(jsonString);
      return {
        isValid: true,
        errors: [],
        warnings: []
      };
    } catch (error: any) {
      const match = error.message.match(/at position (\d+)/);
      let line = 1;
      let column = 1;

      if (match) {
        const position = parseInt(match[1]);
        const lines = jsonString.substring(0, position).split('\n');
        line = lines.length;
        column = lines[lines.length - 1].length + 1;
      }

      return {
        isValid: false,
        errors: [{
          type: 'syntax',
          message: `Erro de sintaxe JSON: ${error.message}`,
          line,
          column
        }],
        warnings: []
      };
    }
  }
}
